import type { ApiResponse } from '@/shared/types';
import api from '@/shared/api/client';

export const PLATFORM_BATCH_TENANT_ID = '00000000-0000-0000-0000-000000000010';

export type BatchImportSubmitResult = {
  batchId: string;
  jobId: string;
  executionId: string;
  totalRows: number;
};

export type BatchProgressResult = {
  executionId: string;
  status: string;
  totalCount: number;
  processedCount: number;
  successCount: number;
  failCount: number;
  progress: number;
  startTime?: string;
  endTime?: string;
  durationMs: number;
  isRunning: boolean;
};

export type BatchImportSummary = {
  batchId: string;
  total: number;
  successCount: number;
  failCount: number;
  pendingCount: number;
};

export type BatchImportErrorRow = {
  id: string;
  rowNo?: number;
  rowData?: Record<string, unknown>;
  errorMessage?: string | null;
};

export const batchApi = {
  submitBatchImport: (
    payload: {
      rows: Record<string, unknown>[];
      jobName?: string;
      processorName?: string;
      fileName?: string;
      mimeType?: string;
      sizeBytes?: number;
    },
    options?: {
      idempotencyKey?: string;
      tenantId?: string;
    }
  ) =>
    api.post<ApiResponse<BatchImportSubmitResult>>('/operator/batch/imports', payload, {
      params: options?.tenantId ? { tenantId: options.tenantId } : undefined,
      headers: options?.idempotencyKey ? { 'Idempotency-Key': options.idempotencyKey } : undefined,
    }),

  getBatchProgress: (executionId: string, tenantId?: string) =>
    api.get<ApiResponse<BatchProgressResult>>(`/operator/batch/executions/${executionId}/progress`, {
      params: tenantId ? { tenantId } : undefined,
    }),

  getImportSummary: (batchId: string, tenantId?: string) =>
    api.get<ApiResponse<BatchImportSummary>>(`/operator/batch/imports/${batchId}/summary`, {
      params: tenantId ? { tenantId } : undefined,
    }),

  getImportErrors: (batchId: string, params?: { tenantId?: string; page?: number; size?: number }) =>
    api.get<ApiResponse<{ list: BatchImportErrorRow[]; pagination: { page: number; size: number; total: number } }>>(
      `/operator/batch/imports/${batchId}/errors`,
      { params },
    ),

  listDemoRecords: (params?: {
    tenantId?: string;
    batchId?: string;
    page?: number;
    size?: number;
  }) =>
    api.get<ApiResponse<{ list: Array<Record<string, unknown>>; pagination: { page: number; size: number; total: number } }>>(
      '/operator/batch/demo-records',
      {
        params: {
          tenantId: params?.tenantId ?? PLATFORM_BATCH_TENANT_ID,
          batchId: params?.batchId,
          page: params?.page,
          size: params?.size,
        },
      },
    ),

  pauseExecution: (executionId: string, tenantId?: string) =>
    api.post<ApiResponse<{ id: string; status: string }>>(
      `/operator/batch/executions/${executionId}/pause`,
      undefined,
      { params: tenantId ? { tenantId } : undefined },
    ),

  resumeExecution: (executionId: string, tenantId?: string) =>
    api.post<ApiResponse<{ id: string; status: string }>>(
      `/operator/batch/executions/${executionId}/resume`,
      undefined,
      { params: tenantId ? { tenantId } : undefined },
    ),

  cancelExecution: (executionId: string, tenantId?: string) =>
    api.post<ApiResponse<{ id: string; status: string }>>(
      `/operator/batch/executions/${executionId}/cancel`,
      undefined,
      { params: tenantId ? { tenantId } : undefined },
    ),
};
